/**
 * UniversalDivisionalToggle - Universal component for all divisional chakra toggles
 *
 * Replaces individual hardcoded toggle blocks for D7, D8, D12, D20, D24, D30, D40, D60
 * with a single reusable component.
 *
 * Features:
 * - Toggle switch for chakra visibility
 * - Start point selector when enabled (except for nakshatra with 'default' start point)
 * - Automatic table rendering
 * - Embedded subdivisions toggle (pada for nakshatra)
 * - Uses useDivisionalChakraSettings hook
 * - Emits ChakraToggleEvent for Panel B integration
 * - Subscribes to Panel B commands
 */

import { devError } from '../../../../../utils/devLog';
import React, { useEffect, useCallback } from 'react';
import { Switch, Box } from '@chakra-ui/react';
import { useDivisionalChakraSettings } from '../../../../../hooks/useDivisionalChakraSettings';
import { useNakshatraPadaSettings } from '../../../../../hooks/useNakshatraPadaSettings';
import { StartPointSelector } from './StartPointSelector';
import { DivisionalChart, DIVISIONAL_CHARTS, getChakraTypeFromId } from '../../../../../config/divisionalCharts';
import { JatakaResponse } from '../../../../../models/api';
import { DivisionalChakraType, getDivisionalChakraConfig } from '../../../../../config/divisionalChakraConfig';
import { CHAKRA_TOGGLE_EVENT_TYPE, ChakraToggleEvent } from '../../universal_architecture/types/EventTypes';
import { globalEventStream } from '../../universal_architecture/core/EventStream';

// Import all table components
import { CharaKarakaInfoTable } from './CharaKarakaInfoTable';
import { AshtamshaInfoTable } from './AshtamshaInfoTable';
import { LakshmiDwadashamshaInfoTable } from './LakshmiDwadashamshaInfoTable';
import { D40DivisionsTable } from './D40DivisionsTable';
import { D24DivisionsTable } from './D24DivisionsTable';
import { D30DivisionsTable } from './D30DivisionsTable';
import { VimsamshaDivisionsInfoTable } from './VimsamshaDivisionsInfoTable';
import { KaranaShashtyamshaInfoTable } from './KaranaShashtyamshaInfoTable';

interface UniversalDivisionalToggleProps {
    toggleLabel: string;        // e.g., "Show D7 Saptamsa"
    colorScheme: string;        // Chakra UI color scheme
    itemId: string;             // Chart item ID (e.g., 'd7-chara-karaka')
    apiResponse?: JatakaResponse | null;
    externalSettings?: ReturnType<typeof useDivisionalChakraSettings>; // Optional: settings from DivisionalChakrasManager
}

/**
 * Map table component names to actual components
 */
const TABLE_COMPONENTS: Record<string, React.ComponentType<{ apiResponse?: JatakaResponse | null }>> = {
    'CharaKarakaInfoTable': CharaKarakaInfoTable,
    'AshtamshaInfoTable': AshtamshaInfoTable,
    'LakshmiDwadashamshaInfoTable': LakshmiDwadashamshaInfoTable,
    'D40DivisionsTable': D40DivisionsTable,
    'D24DivisionsTable': D24DivisionsTable,
    'D30DivisionsTable': D30DivisionsTable,
    'VimsamshaDivisionsInfoTable': VimsamshaDivisionsInfoTable,
    'KaranaShashtyamshaInfoTable': KaranaShashtyamshaInfoTable
};

/**
 * Generate unique toggle ID from itemId
 */
const getToggleId = (itemId: string): string => `${itemId}-toggle`;

export const UniversalDivisionalToggle: React.FC<UniversalDivisionalToggleProps> = ({
    toggleLabel,
    colorScheme,
    itemId,
    apiResponse,
    externalSettings
}) => {
    // Get chakra type from item ID - MUST be before hook calls
    const chakraType = getChakraTypeFromId(itemId) as DivisionalChakraType;

    // Use external settings if provided (from DivisionalChakrasManager), otherwise call hook internally
    // MUST call hook unconditionally to follow React rules
    const internalSettings = useDivisionalChakraSettings(chakraType || 'd7');
    const settings = externalSettings || internalSettings;

    // Pada settings for nakshatra subdivisions (unconditional hook call)
    const padaSettings = useNakshatraPadaSettings();

    // Toggle ID for event addressing
    const toggleId = getToggleId(itemId);
    const chartId = settings.chartId || 'default';

    /**
     * Emit chakra toggle event for Panel B
     */
    const emitToggleEvent = useCallback((enabled: boolean) => {
        const event: ChakraToggleEvent = {
            id: `chakra-toggle-${toggleId}-${Date.now()}`,
            type: CHAKRA_TOGGLE_EVENT_TYPE,
            source: toggleId,
            timestamp: Date.now(),
            from: toggleId,
            to: '*', // Broadcast to all listeners (Panel B)
            data: {
                toggleId,
                toggleName: toggleLabel,
                enabled,
                chartId
            }
        };

        globalEventStream.emit(event);
    }, [toggleId, toggleLabel, chartId]);

    /**
     * Handle toggle change - update visibility and emit event
     */
    const handleToggleChange = useCallback(async (enabled: boolean) => {
        await settings.updateVisibility(enabled);
        emitToggleEvent(enabled);
    }, [settings, emitToggleEvent]);

    /**
     * Subscribe to Panel B commands (enable/disable from Panel B)
     */
    useEffect(() => {
        const subscription = globalEventStream.subscribe(
            CHAKRA_TOGGLE_EVENT_TYPE,
            (event: ChakraToggleEvent) => {
                // Only process events addressed to this toggle
                if (event.to !== toggleId && event.to !== '*') return;
                // Ignore self-emitted events
                if (event.from === toggleId) return;
                // Check chart ID matches
                if (event.data.chartId !== chartId) return;

                // Process command from Panel B
                if (event.from === 'panel-b') {
                    settings.updateVisibility(event.data.enabled);
                }
            }
        );

        return () => subscription?.unsubscribe?.();
    }, [toggleId, chartId, settings]);

    /**
     * Emit initial state on mount (for Panel B to collect enabled toggles)
     */
    useEffect(() => {
        if (settings.showChakra) {
            // Small delay to ensure Panel B is subscribed
            const timer = setTimeout(() => {
                emitToggleEvent(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []); // Only on mount

    // Early return AFTER hooks
    if (!chakraType) {
        devError(`❌ UniversalDivisionalToggle: Unknown chakra type for item ID: ${itemId}`);
        return null;
    }

    // Get chakra config to check for subdivisions
    const config = getDivisionalChakraConfig(chakraType);
    const hasSubdivisions = config.hasSubdivisions && config.subdivisions;
    const showStartPointSelector = true; // Always show start point selector for all chakras

    // Get the table component (optional - some chakras like Muhurta don't have tables)
    let TableComponent: React.ComponentType<{ apiResponse?: JatakaResponse | null }> | null = null;

    // For divisional charts with table components
    const chart = DIVISIONAL_CHARTS.find(c => c.id === itemId);
    if (chart?.tableComponent) {
        TableComponent = TABLE_COMPONENTS[chart.tableComponent] || null;
        if (chart.tableComponent && !TableComponent) {
            devError(`❌ UniversalDivisionalToggle: Table component ${chart.tableComponent} not found`);
        }
    }

    return (
        <div>
            {/* Toggle switch row with inline start point selector */}
            <div className="planet-detail-row">
                <span className="planet-detail-label">
                    {toggleLabel}:
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                        size="sm"
                        isChecked={settings.showChakra}
                        isDisabled={settings.isLoading}
                        onChange={(e) => handleToggleChange(e.target.checked)}
                        colorScheme={colorScheme}
                    />
                    {/* Inline start point selector - shown when enabled (except for 'default' start point) */}
                    {settings.showChakra && showStartPointSelector && (
                        <StartPointSelector
                            currentStartPoint={settings.startPoint}
                            onStartPointChange={settings.updateStartPoint}
                            customSettings={settings.customSettings}
                            onCustomSettingsChange={settings.updateCustomSettings}
                            isDisabled={false}
                            isLoading={settings.isLoading}
                            apiResponse={apiResponse}
                            chakraType={chakraType}
                        />
                    )}
                    {settings.isLoading && <span style={{ fontSize: '12px', color: '#999' }}>...</span>}
                    {settings.error && (
                        <span
                            style={{ fontSize: '12px', color: '#e53e3e', cursor: 'help' }}
                            title={settings.error}
                        >
                            ⚠️
                        </span>
                    )}
                </div>
            </div>

            {/* Subdivisions toggle (e.g., Pada for Nakshatra) - shown when main chakra is enabled */}
            {hasSubdivisions && settings.showChakra && (
                <Box ml={6} opacity={settings.showChakra ? 1 : 0.5}>
                    <div className="planet-detail-row">
                        <span className="planet-detail-label">
                            Show {config.subdivisions!.name}:
                        </span>
                        <Switch
                            size="sm"
                            isChecked={padaSettings.showNakshatraPada}
                            isDisabled={padaSettings.isDisabled || !settings.showChakra || padaSettings.isLoading}
                            onChange={(e) => padaSettings.updateSetting(e.target.checked)}
                            colorScheme="purple"
                        />
                        {padaSettings.isLoading && <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>...</span>}
                    </div>
                </Box>
            )}

            {/* Table component (optional - only if chakra has one) */}
            {TableComponent && <TableComponent apiResponse={apiResponse} />}
        </div>
    );
};
